import type { ApiRequestHelpers } from "./useHttpClient";
import type { BulkEmployeeLimits } from "~/composables/useBulkEmployeeCreation";
import type {
  AgbRegisterEmployeeLookup,
  BulkEmployeesPayload,
  BulkEmployeeReview,
  BulkEmployeeResult,
  CollectionResponse,
  Employee,
  EmployeeCreated,
  EmployeeCreatePayload,
  PractitionerIdentifier,
  EmployeeInvitationsPayload,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  Invitation,
  InvitationIdentifier,
  LinkEmployeesToLocationPayload,
  LocationIdentifier,
  PaginationQuery,
  Response,
  SortByQuery,
} from "~/types/api";

export function createEmployeeApi({ getRequest, postRequest, putRequest, getPaginationRequest }: ApiRequestHelpers) {
  const bulkEmployeeOptions = () =>
    getRequest<Response<BulkEmployeeLimits>>("/employee-management/bulk/options", { omitContextHeaders: true });
  const createBulkEmployees = (data: BulkEmployeesPayload) =>
    postRequest<Response<BulkEmployeeResult>>("/employee-management/bulk", data, {
      omitContextHeaders: true,
      suppressValidationToast: true,
      disableAuthRetry: true,
    });
  const reviewBulkEmployees = (data: BulkEmployeesPayload) =>
    postRequest<Response<BulkEmployeeReview>>("/employee-management/bulk/review", data, {
      omitContextHeaders: true,
      suppressValidationToast: true,
    });
  /** Fetches a paginated list of employees for a location. */
  const employees = (
    locationId: LocationIdentifier,
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<Employee>(`/locations/${locationId}/employees`, pagination, sortBy, includes, filterBy);

  /** Fetches a paginated list of employee invitations for a location. */
  const employeeInvitations = (locationId: LocationIdentifier, pagination: PaginationQuery, sortBy?: SortByQuery) =>
    getPaginationRequest<Invitation>(`/locations/${locationId}/employee-invitations`, pagination, sortBy);

  /** Sends employee invitations for a location. */
  const sendEmployeeInvitations = (locationId: LocationIdentifier, data: EmployeeInvitationsPayload) =>
    postRequest<CollectionResponse<Invitation>>(`/locations/${locationId}/employee-invitations`, data);

  /** Looks up a healthcare provider in the AGB register by full AGB code, for an employee of the location. */
  const lookupEmployeeAgbRegister = (locationId: LocationIdentifier, agbCode: string) =>
    getRequest<Response<AgbRegisterEmployeeLookup>>("/employee-management/agb-register", {
      params: { location_id: locationId, agb_code: agbCode },
      suppressValidationToast: true,
    });

  /** Creates a new employee record. */
  const sendEmployeeCreate = (data: EmployeeCreatePayload) =>
    postRequest<EmployeeCreated>(`/employee-management`, data);

  /** Resends a pending employee invitation. */
  const resendEmployeeInvitation = (locationId: LocationIdentifier, invitationId: InvitationIdentifier) =>
    putRequest<Invitation>(`/locations/${locationId}/employee-invitations/${invitationId}/resend`);

  /** Fetches all employees across the locations associated with a given location. */
  const getAllEmployeesFromLocationIds = (location_id: LocationIdentifier) =>
    postRequest<Response<Partial<Employee>[]>>(`/employee-management/all-employees-from-location-ids`, {
      current_location_id: location_id,
    });

  /** Links existing employees to a location. */
  const linkEmployeesToLocation = (locationId: LocationIdentifier, data: LinkEmployeesToLocationPayload) =>
    postRequest<Response<Employee[]>>(`/locations/${locationId}/link-employee`, data);

  /** Archives staff access throughout the selected organization. */
  const archivePractitioner = (practitionerId: PractitionerIdentifier) =>
    putRequest<EmptyResponse>(`/practitioners/${practitionerId}/archive`);

  return {
    bulkEmployeeOptions,
    createBulkEmployees,
    reviewBulkEmployees,
    employees,
    employeeInvitations,
    sendEmployeeInvitations,
    lookupEmployeeAgbRegister,
    sendEmployeeCreate,
    resendEmployeeInvitation,
    getAllEmployeesFromLocationIds,
    linkEmployeesToLocation,
    archivePractitioner,
  };
}

export type EmployeeApi = ReturnType<typeof createEmployeeApi>;
